import FeeScheduleResponse from '../../model/Fee-schedule.model';
import axiosInstance from '../../utils/axiosInstance';
import { Page } from '../../model/Page.model';

export interface CreateFeeScheduleRequest {
  // Biểu phí dùng chung hoặc đặc thù
  feeRate: number;
  effectiveDate: string;
  cardTypeId: number | null;
  // Bắt buộc cần posDeviceId cho cấu trúc mới
  posDeviceId: number | null;
  // bankId là tùy chọn, nếu có thì là biểu phí đặc thù, nếu không thì là biểu phí dùng chung
  bankId?: number | null;
  description?: string;
}

export interface FeeScheduleHistoryResponse {
  id: number;
  feeScheduleId: number;
  cardType: any;
  bank?: any;
  posDevice: any;
  feeRate: number;
  effectiveDate: string;
  isActive: boolean;
  description?: string;
  action: string;
  changedAt: string;
  oldFeeRate?: number;
  oldEffectiveDate?: string;
}

class FeeScheduleService {
  async getAllFeeSchedules(): Promise<FeeScheduleResponse[]> {
    try {
      const { data } = await axiosInstance.get('/fee-schedules');
      return data;
    } catch (error) {
      console.error('Lỗi khi tải danh sách biểu phí:', error);
      throw new Error(
        'Lỗi khi tải danh sách biểu phí: ' + this.getErrorMessage(error)
      );
    }
  }

  async getDeletedFeeSchedules(
    page: number = 0,
    size: number = 10
  ): Promise<Page<FeeScheduleResponse>> {
    try {
      const { data } = await axiosInstance.get('/fee-schedules/deleted', {
        params: {
          page,
          size,
        },
      });
      return data;
    } catch (error) {
      console.error('Lỗi khi tải danh sách biểu phí đã xóa:', error);
      throw new Error(
        'Lỗi khi tải danh sách biểu phí đã xóa: ' + this.getErrorMessage(error)
      );
    }
  }

  async getFeeScheduleById(id: number): Promise<FeeScheduleResponse> {
    try {
      const { data } = await axiosInstance.get(`/fee-schedules/${id}`);
      return data;
    } catch (error) {
      console.error(`Lỗi khi tải biểu phí ${id}:`, error);
      throw new Error(`Lỗi khi tải biểu phí: ` + this.getErrorMessage(error));
    }
  }

  async createFeeSchedule(
    feeSchedule: CreateFeeScheduleRequest
  ): Promise<FeeScheduleResponse> {
    try {
      const { data } = await axiosInstance.post(
        '/fee-schedules/create',
        feeSchedule
      );
      return data;
    } catch (error) {
      console.error('Lỗi khi tạo biểu phí:', error);
      throw new Error('Lỗi khi tạo biểu phí: ' + this.getErrorMessage(error));
    }
  }

  async updateFeeSchedule(
    id: number,
    feeSchedule: CreateFeeScheduleRequest
  ): Promise<FeeScheduleResponse> {
    try {
      console.log('🌐 FeeScheduleService.updateFeeSchedule called with:', {
        id,
        feeSchedule,
      }); // Debug log
      const { data } = await axiosInstance.put(
        `/fee-schedules/edit/${id}`,
        feeSchedule
      );
      console.log('✅ FeeScheduleService.updateFeeSchedule response:', data); // Debug log
      return data;
    } catch (error) {
      console.error(
        `❌ FeeScheduleService.updateFeeSchedule error for ID ${id}:`,
        error
      ); // Debug log
      console.error(`Lỗi khi cập nhật biểu phí ${id}:`, error);
      throw new Error(
        `Lỗi khi cập nhật biểu phí: ` + this.getErrorMessage(error)
      );
    }
  }

  async softDeleteFeeSchedule(id: number): Promise<void> {
    try {
      await axiosInstance.delete(`/fee-schedules/soft-delete/${id}`);
    } catch (error) {
      console.error(`Lỗi khi xóa mềm biểu phí ${id}:`, error);
      throw new Error(
        `Lỗi khi xóa mềm biểu phí: ` + this.getErrorMessage(error)
      );
    }
  }

  /**
   * Lấy biểu phí theo thiết bị POS và loại thẻ
   * @param posDeviceId ID của thiết bị POS
   * @param cardTypeId ID của loại thẻ
   * @returns Danh sách biểu phí cho thiết bị POS và loại thẻ
   */
  async getFeeSchedulesByPosDeviceIdAndCardTypeId(
    posDeviceId: number,
    cardTypeId: number
  ): Promise<FeeScheduleResponse[]> {
    try {
      const { data } = await axiosInstance.get(
        `/fee-schedules/pos/${posDeviceId}/card-type/${cardTypeId}`
      );
      return data;
    } catch (error) {
      console.error(`Lỗi khi tải biểu phí theo loại thẻ ${cardTypeId}:`, error);
      throw new Error(
        `Lỗi khi tải biểu phí theo loại thẻ: ` + this.getErrorMessage(error)
      );
    }
  }

  /**
   * Lấy danh sách biểu phí theo thiết bị POS
   * @param posDeviceId ID của thiết bị POS
   * @returns Danh sách biểu phí cho thiết bị POS
   */
  async getFeeSchedulesByPosDevice(
    posDeviceId: number
  ): Promise<FeeScheduleResponse[]> {
    try {
      const { data } = await axiosInstance.get(
        `/fee-schedules/pos/${posDeviceId}`
      );
      return data;
    } catch (error) {
      console.error(`Lỗi khi tải biểu phí theo POS ${posDeviceId}:`, error);
      throw new Error(
        `Lỗi khi tải biểu phí theo POS: ` + this.getErrorMessage(error)
      );
    }
  }

  async getActiveFeeSchedulesByPos(): Promise<{ [posId: string]: number[] }> {
    try {
      const { data } = await axiosInstance.get(`/fee-schedules/active-by-pos`);
      return data;
    } catch (error) {
      console.error('Lỗi khi tải biểu phí đang hoạt động theo POS:', error);
      throw new Error(
        'Lỗi khi tải biểu phí đang hoạt động theo POS: ' +
          this.getErrorMessage(error)
      );
    }
  }

  /**
   * Tìm biểu phí phù hợp cho loại thẻ và POS
   * @param cardTypeId ID của loại thẻ
   * @param posDeviceId ID của thiết bị POS
   * @returns Mức phí áp dụng
   */
  async findApplicableFeeRate(
    cardTypeId: number,
    posDeviceId: number
  ): Promise<number> {
    try {
      const { data } = await axiosInstance.get(
        `/fee-schedules/applicable-fee/${cardTypeId}/${posDeviceId}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tìm biểu phí cho thẻ ${cardTypeId} và POS ${posDeviceId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tìm biểu phí phù hợp: ` + this.getErrorMessage(error)
      );
    }
  }

  /**
   * Tìm biểu phí phù hợp cho loại thẻ, POS và ngân hàng cụ thể
   * @param cardTypeId ID của loại thẻ
   * @param posDeviceId ID của thiết bị POS
   * @param bankId ID của ngân hàng
   * @returns Mức phí áp dụng
   */
  async findApplicableFeeRateWithBank(
    cardTypeId: number,
    posDeviceId: number,
    bankId: number
  ): Promise<number> {
    try {
      const { data } = await axiosInstance.get(
        `/fee-schedules/applicable-fee/${cardTypeId}/${posDeviceId}/${bankId}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tìm biểu phí cho thẻ ${cardTypeId}, POS ${posDeviceId} và ngân hàng ${bankId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tìm biểu phí phù hợp: ` + this.getErrorMessage(error)
      );
    }
  }

  /**
   * Lấy lịch sử các biểu phí của một thiết bị POS và loại thẻ
   * @param posDeviceId ID của thiết bị POS
   * @param cardTypeId ID của loại thẻ
   * @returns Danh sách các biểu phí, bao gồm cả các biểu phí không còn hoạt động
   */
  async getFeeScheduleHistory(
    posDeviceId: number,
    cardTypeId: number
  ): Promise<FeeScheduleResponse[]> {
    try {
      const { data } = await axiosInstance.get(
        `/fee-schedules/history/pos/${posDeviceId}/card-type/${cardTypeId}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải lịch sử biểu phí cho POS ${posDeviceId} và loại thẻ ${cardTypeId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải lịch sử biểu phí: ` + this.getErrorMessage(error)
      );
    }
  }

  /**
   * Lấy lịch sử chi tiết của các thay đổi biểu phí theo thiết bị POS và loại thẻ
   * @param posDeviceId ID của thiết bị POS
   * @param cardTypeId ID của loại thẻ
   * @returns Danh sách chi tiết các thay đổi biểu phí
   */
  async getFeeScheduleHistoryDetails(
    posDeviceId: number,
    cardTypeId: number
  ): Promise<FeeScheduleHistoryResponse[]> {
    try {
      const { data } = await axiosInstance.get(
        `/fee-schedules/history/details/pos/${posDeviceId}/card-type/${cardTypeId}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải lịch sử chi tiết biểu phí cho POS ${posDeviceId} và loại thẻ ${cardTypeId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải lịch sử chi tiết biểu phí: ` + this.getErrorMessage(error)
      );
    }
  }

  /**
   * Lấy lịch sử chi tiết của một biểu phí cụ thể
   * @param feeScheduleId ID của biểu phí
   * @returns Danh sách chi tiết các thay đổi
   */
  async getFeeScheduleHistoryById(
    feeScheduleId: number
  ): Promise<FeeScheduleHistoryResponse[]> {
    try {
      const { data } = await axiosInstance.get(
        `/fee-schedules/history/details/${feeScheduleId}`
      );
      return data;
    } catch (error) {
      console.error(
        `Lỗi khi tải lịch sử chi tiết của biểu phí ${feeScheduleId}:`,
        error
      );
      throw new Error(
        `Lỗi khi tải lịch sử chi tiết biểu phí: ` + this.getErrorMessage(error)
      );
    }
  }

  // Helper method to extract error messages
  private getErrorMessage(error: any): string {
    return (
      error.response?.data?.message || error.message || 'Lỗi không xác định'
    );
  }
}

export default new FeeScheduleService();
